import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Container } from '../components/ui/Container'
import { Button } from '../components/ui/Button'
import InteractiveDemo from '../components/InteractiveDemo'
import { ProfessionalHero } from '../components/ProfessionalHero'
import BiotuneLoopTimeline from '../components/BiotuneLoopTimeline'
import TestingModulesSection from '../components/TestingModulesSection'
import SupplementSection from '../components/SupplementSection'
import NutritionSection from '../components/NutritionSection'
import YogaSection from '../components/YogaSection'
import WorkoutSection from '../components/WorkoutSection'
import MealmateSection from '../components/mealmate/MealmateSection'
import AIVisual from '../components/ui/AIVisual'
import ProblemOpportunitySection from '../components/pitch-deck/ProblemOpportunitySection'
import SevenPillarsSection from '../components/pitch-deck/SevenPillarsSection'
import BusinessModelSection from '../components/pitch-deck/BusinessModelSection'
import MarketVisionSection from '../components/pitch-deck/MarketVisionSection'
import FutureExpansionSection from '../components/pitch-deck/FutureExpansionSection'
import {
  ArrowRight,
  CheckCircle,
  Sun,
  Smartphone,
  Monitor,
  Watch,
  HeartPulse as HeartPulseIcon,
  Accessibility,
  Eye,
  Volume2,
  Keyboard,
  Globe
} from 'lucide-react'

import PageLayout from '../components/layout/PageLayout'

const BioTuneHomepage: React.FC = () => {
  // What you get features
  const whatYouGet = [
    {
      title: "Interactive Dashboard",
      description: "Beautiful, easy-to-understand visualizations of your health data",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800&aspect_ratio=16:9",
      fallback: "tech" as const
    },
    {
      title: "Trend Tracking",
      description: "See how your biomarkers change over time with detailed trend analysis",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800&aspect_ratio=16:9",
      fallback: "tech" as const
    },
    {
      title: "AI-Powered Insights",
      description: "Personalized recommendations based on your unique health profile",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800&aspect_ratio=16:9",
      fallback: "tech" as const
    },
    {
      title: "Expert Support",
      description: "Access to health coaches and medical professionals for guidance",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80&w=800&aspect_ratio=16:9",
      fallback: "health" as const
    }
  ]

  return (
    <PageLayout>

      {/* Professional Hero Section */}
      <ProfessionalHero />

      {/* Slide 1: Problem & Opportunity */}
      <ProblemOpportunitySection />

      {/* Slide 2: The Core Ecosystem (7 Pillars) */}
      <SevenPillarsSection />

      {/* Interactive Demo Section */}
      <InteractiveDemo />

      {/* Slide 3: The Market Vision */}
      <MarketVisionSection />

      {/* Future Expansion - AI Health Kiosks */}
      <FutureExpansionSection />

      {/* Slide 4: Business Strategy & Economics */}
      <BusinessModelSection />

      {/* Biotune Loop Timeline */}
      <BiotuneLoopTimeline />

      {/* Testing Modules Section */}
      <TestingModulesSection />

      {/* Supplement Section */}
      <SupplementSection />

      {/* Nutrition Section */}
      <NutritionSection />

      {/* Yoga Section */}
      <YogaSection />

      {/* Workout Section */}
      <WorkoutSection />

      {/* Mealmate Integration Section */}
      <MealmateSection />

      {/* Device Compatibility & Accessibility Section */}
      <section className="section-padding bg-theme-bg">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold gradient-heading mb-6">
              Access Your Health Data Anywhere
            </h2>
            <p className="text-xl text-theme-text-secondary max-w-3xl mx-auto leading-relaxed">
              BioTune works seamlessly across all your devices with comprehensive accessibility features and medical device integration.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {/* Mobile Platforms */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-theme-surface/70 p-8 rounded-2xl border border-theme-border hover:border-primary-400 dark:hover:border-primary-500 transition-all duration-300 group"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mr-4">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-theme-text-primary group-hover:text-theme-primary transition-colors">Mobile Platforms</h3>
              </div>
              <ul className="space-y-3 text-theme-text-secondary">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>iOS 14.0+ (iPhone & iPad)</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Android 8.0+ (Phones & Tablets)</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-4 h-4 text-amber-500">🕐</div>
                  <span>Native iOS/Android Apps - <strong className="text-amber-600 dark:text-amber-400">Coming Q1 2027</strong></span>
                </li>
              </ul>
            </motion.div>

            {/* Medical Devices */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-theme-surface/70 p-8 rounded-2xl border border-theme-border hover:border-theme-primary transition-all duration-300 group"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center mr-4">
                  <HeartPulseIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-theme-text-primary group-hover:text-theme-primary transition-colors">Medical Devices</h3>
              </div>
              <ul className="space-y-3 text-theme-text-secondary">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Blood Glucose Monitors</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Blood Pressure Cuffs</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Digital Scales</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Digital Thermometers</span>
                </li>
              </ul>
            </motion.div>

            {/* Wearables */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-theme-surface/70 p-8 rounded-2xl border border-theme-border hover:border-purple-400 dark:hover:border-purple-500 transition-all duration-300 group"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4">
                  <Watch className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-theme-text-primary group-hover:text-theme-primary transition-colors">Wearables</h3>
              </div>
              <ul className="space-y-3 text-theme-text-secondary">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Apple Watch (Series 4+)</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Oura Ring (Gen 2+)</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Whoop Strap 4.0</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Garmin Devices</span>
                </li>
              </ul>
            </motion.div>

            {/* Web Platform */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-theme-surface/70 p-8 rounded-2xl border border-theme-border hover:border-green-400 dark:hover:border-green-500 transition-all duration-300 group"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mr-4">
                  <Monitor className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-theme-text-primary group-hover:text-theme-primary transition-colors">Web Platform</h3>
              </div>
              <ul className="space-y-3 text-theme-text-secondary">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Chrome 90+</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Safari 14+</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Firefox 88+</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Edge 90+</span>
                </li>
              </ul>
            </motion.div>

            {/* Accessibility */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-theme-surface/70 p-8 rounded-2xl border border-theme-border hover:border-amber-400 dark:hover:border-amber-500 transition-all duration-300 group"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center mr-4">
                  <Accessibility className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-theme-text-primary group-hover:text-theme-primary transition-colors">Accessibility</h3>
              </div>
              <ul className="space-y-3 text-theme-text-secondary">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>WCAG 2.1 AA Compliant</span>
                </li>
                <li className="flex items-center space-x-3">
                  <Eye className="w-4 h-4 text-blue-500" />
                  <span>Screen Reader Support</span>
                </li>
                <li className="flex items-center space-x-3">
                  <Volume2 className="w-4 h-4 text-blue-500" />
                  <span>Voice Navigation</span>
                </li>
                <li className="flex items-center space-x-3">
                  <Keyboard className="w-4 h-4 text-blue-500" />
                  <span>Keyboard Navigation</span>
                </li>
              </ul>
            </motion.div>

            {/* Theme Compatibility */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              viewport={{ once: true }}
              className="bg-theme-surface/70 p-8 rounded-2xl border border-theme-border hover:border-indigo-400 dark:hover:border-indigo-500 transition-all duration-300 group"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                  <Sun className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-theme-text-primary group-hover:text-theme-primary transition-colors">Theme Support</h3>
              </div>
              <ul className="space-y-3 text-theme-text-secondary">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Light Mode</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Dark Mode</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>System Preference</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>High Contrast Mode</span>
                </li>
              </ul>
            </motion.div>
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            viewport={{ once: true }}
            className="text-center bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-8 md:p-12 text-white"
          >
            <Globe className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h3 className="text-3xl font-bold mb-4">Ready to Transform Your Health Journey?</h3>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Built for people who want to take a more proactive role in their health. See which Health OS plan fits your monthly lab needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing">
                <Button size="lg" variant="secondary" className="bg-theme-surface text-theme-primary hover:bg-theme-surface-light">
                  View Plans & Pricing
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary-600">
                View Compatibility Guide
              </Button>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* What You Get Section */}
      <section className="section-padding bg-theme-bg">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold gradient-heading mb-6">
              Everything You Need to Understand Your Health
            </h2>
            <p className="text-xl text-theme-text-secondary max-w-3xl mx-auto leading-relaxed">
              Transform complex health data into clear, actionable insights with our comprehensive platform
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whatYouGet.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative h-full"
              >
                <div className="bg-theme-surface/70 p-6 rounded-2xl border border-theme-border hover:border-primary-400 dark:hover:border-primary-500 transition-all duration-300 h-full group">
                  <div className="mb-6 overflow-hidden rounded-lg">
                    <AIVisual
                      src={item.image}
                      alt={item.title}
                      fallbackType={item.fallback}
                      className="w-full h-32"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-theme-text-primary group-hover:text-theme-primary mb-3">
                    {item.title}
                  </h3>
                  <p className="text-theme-text-secondary group-hover:text-theme-text-primary leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>
    </PageLayout>
  )
}

export default BioTuneHomepage
